import React from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import {
  QuestionNonValue,
  Serializer,
  ElementFactory,
  ItemValue,
} from "survey-core";

export class SuggestionsQuestionModel extends QuestionNonValue {
  static type = "suggestions";

  constructor(name: string) {
    super(name);
    this.createItemValues("suggestions");
  }

  getType() {
    return SuggestionsQuestionModel.type;
  }

  get suggestions() {
    return this.getPropertyValue("suggestions");
  }

  set suggestions(val) {
    this.setPropertyValue("suggestions", val);
  }

  get suggestionList(): string[] {
    const items = this.suggestions || [];
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          return item.text || item.value || "";
        }
        return "";
      })
      .filter((s) => s);
  }

  public get name(): string {
    return this.getPropertyValue("name");
  }

  public set name(val: string) {
    this.setPropertyValue("name", val);
  }

  public toJSON(): object {
    const json = super.toJSON();
    const suggestions = this.getPropertyValue("suggestions");

    if (suggestions && Array.isArray(suggestions)) {
      json.suggestions = suggestions.map(
        (item: ItemValue | string | { value: string; text: string }) => {
          if (typeof item === "string") {
            return item;
          }

          if (item && typeof item === "object") {
            if (item.text === item.value || !item.text) {
              return item.value;
            }

            return { value: item.value, text: item.text };
          }

          return item;
        },
      );
    }

    return json;
  }
}

export type SuggestionsQuestionComponentProps = {
  question: SuggestionsQuestionModel;
};

export function SuggestionsQuestionComponent({
  question,
}: SuggestionsQuestionComponentProps) {
  if (question.suggestionList.length === 0) {
    return null;
  }

  return (
    <div
      className={question.cssClasses?.root}
      style={{
        backgroundColor: "#f0f9ff",
        border: "1px solid #bfdbfe",
        borderRadius: "4px",
        padding: "10px",
        marginBottom: "10px",
      }}
    >
      <h4 style={{ margin: "0 0 6px 0", fontSize: "14px", fontWeight: "600" }}>
        💡 Suggestions
      </h4>
      <ul style={{ margin: "0", paddingLeft: "16px", lineHeight: "1.3" }}>
        {question.suggestionList.map((suggestion, index) => (
          <li key={index} style={{ fontSize: "13px", marginBottom: "2px" }}>
            {suggestion}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function registerSuggestionsQuestion() {
  ElementFactory.Instance.registerElement(
    "suggestions",
    (name: string) => new SuggestionsQuestionModel(name),
  );

  Serializer.addClass(
    "suggestions",
    [
      {
        name: "suggestions",
        category: "general",
        displayName: "Suggestions",
        type: "itemvalues",
        isRequired: true,
        default: [],
        isSerializable: true,
        visibleIndex: 0,
      },
    ],
    () => new SuggestionsQuestionModel(""),
    "nonvalue",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    SuggestionsQuestionModel.type,
    (propsOrName: string | SuggestionsQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new SuggestionsQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(SuggestionsQuestionComponent, props);
    },
  );
}
